import { Box, IconButton as IB, IconButtonProps as Props, Menu, MenuButton, MenuList, useDisclosure } from "@chakra-ui/react";
import { AuthenticateSVG, ContactSupportSVG, KycSVG, PolicySVG, TermsSVG, TransferSVG } from "assets";
import Icon, { IconNames, IconProps } from "components/Icon/Icon";
import { useAuth, useSupport } from "hooks";
import configs from "config";
import { useLocation, useNavigate } from "react-router";
import { IconMenuItem, DeleteAccount, ClearCurrentSession } from "components";
import ls from "utils/secureStorage";
import { useEffect, useMemo } from "react";
import { AuthPayload } from "store/slices/auth";
import { BsBank } from "react-icons/bs";

interface IconButtonProps extends Omit<Props, "icon"> {
  icon: IconNames;
  _icon?: Omit<IconProps, "type">;
}

export function IconButton(props: IconButtonProps) {
  const { icon, _icon, ...xprops } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { auth } = useAuth();

  const { bootWithProps, show } = useSupport();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleSupport = () => {
    show();
  };

  const location = useLocation();

  const handleAuthenticate = () => {
    ls.set(configs.AUTHENTICATE_CLICK, true);
    ls.set(configs.PERSIST_URL, location.search);

    navigate("/checkout");
  };

  const authPayload = useMemo(() => (auth ? (auth as AuthPayload) : undefined!), [auth]);
  const currentTime = new Date().getTime();

  useEffect(() => {
    bootWithProps();
    if (currentTime > authPayload?.exp! && location.pathname !== "/") {
      const URL = ls.get(configs.PERSIST_URL);
      window.location.replace(`/${URL}`);

      ls.remove(configs.AUTH_TOKEN_KEY);
    }
  }, [authPayload?.exp, bootWithProps, currentTime, location.pathname]);

  const module = ls.get("module");

  const cicoAuth = ls.get(configs.CICO_TOKEN_KEY);

  return (
    <Box>
      <Menu isOpen={isOpen} onClose={onClose} preventOverflow>
        <MenuButton></MenuButton>
        <IB
          onClick={onOpen}
          minW="unset"
          minH="unset"
          boxSize="30px"
          variant="transparent"
          icon={<Icon type={icon} {..._icon} />}
          {...xprops}
        />

        <Box>
          <MenuList
            fontSize={{ base: "12px", md: "14px" }}
            color="#303131"
            bg="#F2F4F3"
            borderRadius="3px"
            minW="185px"
            height="auto"
            padding="18px 0"
            border="none"
            transform="translateX(-150%)"
          >
            <Box padding="0 8px 8px">
              <IconMenuItem onClick={handleSupport} svg={<ContactSupportSVG />} title="Support" />

              {!auth && location.pathname === "/" && (
                <IconMenuItem svg={<AuthenticateSVG />} title="Authenticate" onClick={handleAuthenticate} />
              )}

              <IconMenuItem
                onClick={() => window.open("https://bitmama.io/terms", "_blank")}
                svg={<TermsSVG />}
                title="Terms of Service"
              />

              <IconMenuItem
                onClick={() => window.open("https://bitmama.io/privacy", "_blank")}
                svg={<PolicySVG />}
                title="Privacy Policy"
              />
            </Box>

            {(!module || module === "wp") && pathname !== "/cico" && authPayload && (
              <>
                <hr />
                <Box padding="0 8px">
                  <IconMenuItem svg={<KycSVG />} title={`KYC Status`} onClick={() => navigate("/kyc-status")} />

                  <IconMenuItem svg={<TransferSVG />} title="Transfer History" onClick={() => navigate("/transfer-history")} />

                  <IconMenuItem
                    svg={<BsBank fontSize="16px" color="#0D4740" />}
                    title="Fiat Account"
                    onClick={() => navigate("/fiat-account")}
                    ml="2px"
                  />
                </Box>
                <hr />

                <Box padding="4px 12px 0 12px">
                  <ClearCurrentSession />

                  <DeleteAccount />
                </Box>
              </>
            )}

            {(module === "wc" || pathname === "/cico") && cicoAuth && (
              <>
                <hr />
                <Box padding="0 8px">
                  <IconMenuItem svg={<KycSVG />} title="CICO KYC" onClick={() => navigate("/cico/kyc")} />

                  <IconMenuItem svg={<TransferSVG />} title="CICO Transfer" onClick={() => navigate("/cico/transfer")} />

                  <ClearCurrentSession />
                </Box>
              </>
            )}
          </MenuList>
        </Box>
      </Menu>
    </Box>
  );
}
